import { Popup } from 'components/common/popup/Popup';
import React, { useEffect, useState } from 'react';
import { instance } from 'api/api';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import useBundleTranslation from 'i18n';
import { ExportResponse } from 'app/content/export/index';
import MITable from 'components/common/grid/MITable';
import { GridData } from 'components/common/grid';
import StaticInfo from 'components/common/StaticInfo';
import pollingService from 'tools/pollingService';
import { useMiNavigation } from 'hooks/useNavigation';

export default function ExportResultPopup({
    popupOpen,
    setPopupOpen,
    formData,
}: {
    popupOpen: boolean;
    setPopupOpen: (state: boolean) => void;
    formData: { [key: string]: any };
}) {
    const { t } = useBundleTranslation('components/common/export');
    const navigate = useMiNavigation();
    const [isVisible, setIsVisible] = useState(false);
    const [exportResultData, setExportResultData] = useState<false | ExportResponse>(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (popupOpen) {
            initExport();
        } else {
            // Give time for the popup to disappear
            window.setTimeout(() => {
                setIsVisible(false);
            }, 100);
        }
    }, [popupOpen]);

    const onSuccess = (data: any, responsePoolingData: any) => {
        setExportResultData(data.data);
        setIsLoading(false);
        setIsVisible(true);
    };

    const onError = (response: { message: string }) => {
        alert(response.message);
        setIsLoading(false);
    };

    const initExport = () => {
        setIsLoading(true);
        instance.post('/data/admin/content/export/result', formData).then((response) => {
            if (response.data.status === 'QUEUE') {
                const { run } = pollingService({
                    onSuccess,
                    onError,
                    requestData: {},
                });

                run(response.data.data.ppl_id);
            }
        });
    };

    if (isLoading) {
        return <LoadingPlaceholder />;
    }

    return isVisible ? (
        <Popup
            onHide={() => {
                setPopupOpen(false);
                navigate('/admin/content');
            }}
            open={popupOpen}
            settings={{ title: t('export_results'), noCancel: true, textOK: t('done') }}
        >
            {exportResultData && !isLoading && <ExportResult exportResult={exportResultData} />}
        </Popup>
    ) : null;
}

function ExportResult({ exportResult }: { exportResult: false | ExportResponse }) {
    const { t } = useBundleTranslation('components/common/export');
    if (exportResult == false) {
        return <StaticInfo type={'error'} label={t('none_elements')}></StaticInfo>;
    }
    const messageBox = exportResult.failed > 0 ? t('has_failed', { count: exportResult.success }) : t('has_success');

    const [gridData, setGridData] = useState<GridData>([]);
    useEffect(() => {
        setGridData(
            exportResult.grid.map((row) => {
                let message = t('success');
                // TODO: add decoration ?
                if (row.name == 'Note') {
                    message = row.message;
                } else {
                    if (row.status != 'success') {
                        message = row.message;
                    }
                }
                return {
                    num: String(row.num),
                    name: row.name,
                    message: message,
                };
            }),
        );
    }, [exportResult.grid]);

    return (
        <div>
            <StaticInfo type={exportResult.failed > 0 ? 'info' : 'success'} label={messageBox} />
            <br />
            <MITable
                gridName={'export_results'}
                autoHeight
                data={gridData}
                isLoading={false}
                columns={[
                    { title: '#', name: 'num' },
                    { title: t('name'), name: 'name' },
                    { title: t('message'), name: 'message' },
                ]}
                massActionsConfig={{ enabled: false }}
            />
            <a href={`/data/admin/content/download/${exportResult.exportImportId}`}>{t('download')}</a>
        </div>
    );
}
